import pipelineActivitiesAPI from 'dashboard/api/pipeline/activities';
import { createAccountRequestGuard } from './utils/accountRequestGuard';

// Auditoria 2026-08-08 (G1): o backend expõe 12 ações de atividades do CRM e o
// dashboard não tinha store nem camada de API para elas. Este módulo é o
// segundo passo do item (a API veio antes); a tela vem depois.
//
// Os guards NÃO são opcionais: `createAccountRequestGuard` descarta a resposta
// de uma conta que já não é a ativa. Sem ele, trocar de conta com um request em
// voo escreve as atividades da conta A no estado que a conta B está exibindo —
// vazamento entre tenants pelo front, invisível para a suíte de backend.
const fetchRequestGuard = createAccountRequestGuard();

const normalizeAccountId = accountId => {
  if (accountId === undefined || accountId === null) return null;
  const normalized = String(accountId).trim();
  return normalized || null;
};

const requireAccountId = (rootGetters, accountId) => {
  const resolved = normalizeAccountId(
    accountId === undefined ? rootGetters.getCurrentAccountId : accountId
  );
  if (resolved === null) throw new TypeError('accountId is required');
  return resolved;
};

// Cross-review Codex: numa ESCRITA, cair na conta ativa quando o chamador não
// informa nada torna a checagem tautológica — "é a conta ativa?" responde sim
// por construção. Quem escreve tem de dizer em qual conta está escrevendo, e o
// valor é capturado pela TELA, não pelo store no instante do dispatch.
const requireExplicitAccountId = (rootGetters, accountId) => {
  if (accountId === undefined || accountId === null) {
    throw new TypeError('accountId must be explicit on writes');
  }
  const resolved = requireAccountId(rootGetters, accountId);
  assertAccountIsActive(rootGetters, resolved);
  return resolved;
};

const accountIsCurrent = (rootGetters, accountId) => {
  const active = normalizeAccountId(rootGetters.getCurrentAccountId);
  return accountId !== null && active !== null && active === accountId;
};

// Cross-review Codex: numa ESCRITA, verificar a conta só depois da resposta é
// tarde — o efeito já aconteceu no servidor. Um dispatch atrasado carregando a
// conta A enquanto B está ativa precisa ser recusado ANTES de sair daqui.
const assertAccountIsActive = (rootGetters, accountId) => {
  if (accountIsCurrent(rootGetters, accountId)) return;

  throw new TypeError('accountId is no longer the active account');
};

// Cross-review Codex (blocker): não basta a conta estar ativa — a LISTA em
// memória pode ser de outra. Criar em B enquanto a lista carregada ainda é de A
// inseriria a atividade nova na coleção do tenant errado.
//
// Cross-review Codex: uma escrita NÃO define a posse da lista. Deixar a
// criação reivindicar uma lista ainda não carregada produzia uma coleção com um
// item só, marcada como "completa" daquela conta — e a leitura seguinte podia
// nem acontecer. Quem define a posse é a LEITURA; a escrita só entra numa lista
// que já é comprovadamente da mesma conta.
const writeBelongsToLoadedList = ($state, accountId) =>
  $state.loadedAccountId === accountId;

// Cross-review Codex: `action` vem do payload e era usado direto como
// propriedade da API — qualquer nome invocaria qualquer método do cliente.
// Só estas quatro são transições.
const TRANSITIONS = Object.freeze([
  'start',
  'complete',
  'cancel',
  'reschedule',
]);

const state = {
  requestEpoch: 0,
  isStale: false,
  requestedAccountId: null,
  // Cross-review Codex: toda operação nasce carimbada com esta versão. Uma
  // leitura que partiu antes de uma escrita NÃO pode sobrescrever o resultado
  // dela, e um `finally` de request da conta anterior não pode mexer nos
  // contadores da conta nova. Comparar o carimbo resolve os dois.
  dataVersion: 0,
  epoch: 0,
  pending: {},
  loadedAccountId: null,
  activities: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

// Cross-review Codex: guardar o dado da conta certa não basta — o getter também
// precisa recusá-lo quando a conta ativa mudou. Entre a troca de conta e a
// resposta da nova carga, a lista antiga continuaria na tela.
const activitiesForActiveAccount = (
  $state,
  _getters,
  _rootState,
  rootGetters
) =>
  accountIsCurrent(rootGetters || {}, $state.loadedAccountId)
    ? $state.activities
    : [];

const getters = {
  allActivities: activitiesForActiveAccount,
  uiFlags: $state => $state.uiFlags,
  // Pendentes primeiro e por data — é como a agenda lê.
  pendingActivities: (...args) =>
    activitiesForActiveAccount(...args)
      .filter(activity => activity.status === 'pending')
      .sort((a, b) =>
        String(a.scheduled_at || '').localeCompare(String(b.scheduled_at || ''))
      ),
};

const mutations = {
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  // Cross-review Codex: isto usava o MESMO `requestEpoch` dos guards de
  // escrita, então uma criação bem-sucedida invalidava a si própria no
  // `finally` e deixava `isCreating` preso. A listagem tem contador próprio.
  // Cross-review Codex: um contador único para todas as escritas fazia a última
  // a terminar limpar a flag ERRADA — uma criação sobrepondo uma transição
  // apagava `isUpdating` com o update ainda em voo. Cada flag conta as suas.
  BUMP_VERSION($state) {
    $state.dataVersion += 1;
  },
  // Cross-review Codex: contadores que atravessam o RESET são o problema —
  // uma operação da conta anterior decrementa o contador da conta nova. Cada
  // época tem o SEU balde; ao trocar de conta, a época muda e o balde antigo é
  // descartado inteiro. A operação atrasada mexe num balde que ninguém lê.
  BEGIN_WRITE($state, { flag, epoch }) {
    const bucket = $state.pending[epoch] || {};
    $state.pending = {
      ...$state.pending,
      [epoch]: { ...bucket, [flag]: (bucket[flag] || 0) + 1 },
    };
    if (epoch === $state.epoch) {
      $state.uiFlags = { ...$state.uiFlags, [flag]: true };
    }
  },
  // Cross-review Codex: eu usava a MESMA versão para duas coisas diferentes —
  // decidir se uma leitura pode aplicar seu retrato E validar a limpeza dos
  // contadores. Com isso a própria escrita invalidava o seu `finally` e o
  // contador nunca voltava a zero. A limpeza é SIMÉTRICA: quem incrementou
  // decrementa, sempre. Só a aplicação do retrato consulta a versão.
  END_WRITE($state, { flag, epoch }) {
    const bucket = $state.pending[epoch] || {};
    const remaining = Math.max(0, (bucket[flag] || 0) - 1);
    $state.pending = {
      ...$state.pending,
      [epoch]: { ...bucket, [flag]: remaining },
    };
    if (epoch === $state.epoch && remaining === 0) {
      $state.uiFlags = { ...$state.uiFlags, [flag]: false };
    }
  },
  BEGIN_FETCH($state, epoch) {
    const bucket = $state.pending[epoch] || {};
    $state.pending = {
      ...$state.pending,
      [epoch]: { ...bucket, isFetching: (bucket.isFetching || 0) + 1 },
    };
    if (epoch === $state.epoch) {
      $state.uiFlags = { ...$state.uiFlags, isFetching: true };
    }
  },
  END_FETCH($state, epoch) {
    const bucket = $state.pending[epoch] || {};
    const remaining = Math.max(0, (bucket.isFetching || 0) - 1);
    $state.pending = {
      ...$state.pending,
      [epoch]: { ...bucket, isFetching: remaining },
    };
    if (epoch === $state.epoch && remaining === 0) {
      $state.uiFlags = { ...$state.uiFlags, isFetching: false };
    }
  },
  // Cross-review Codex: invalidar a listagem em voo perdia o resultado dela sem
  // pôr nada no lugar, nas duas ordens de chegada. A escrita agora só marca que
  // a lista precisa ser relida — quem relê é a tela, com uma leitura de verdade.
  MARK_STALE($state) {
    $state.isStale = true;
  },
  CLEAR_STALE($state) {
    $state.isStale = false;
  },
  SET_REQUESTED_ACCOUNT($state, accountId) {
    $state.requestedAccountId = accountId;
  },
  SET_LOADED_ACCOUNT($state, accountId) {
    $state.loadedAccountId = accountId;
  },
  SET_ACTIVITIES($state, activities) {
    $state.activities = Array.isArray(activities) ? activities : [];
  },
  // Cross-review Codex: uma listagem concorrente pode já ter trazido a
  // atividade recém-criada; empilhar sem checar mostrava o item duplicado.
  ADD_ACTIVITY($state, activity) {
    if (!activity?.id) return;
    if ($state.activities.some(item => item.id === activity.id)) {
      $state.activities = $state.activities.map(item =>
        item.id === activity.id ? activity : item
      );
      return;
    }
    $state.activities = [activity, ...$state.activities];
  },
  REPLACE_ACTIVITY($state, activity) {
    if (!activity?.id) return;
    $state.activities = $state.activities.map(item =>
      item.id === activity.id ? activity : item
    );
  },
  // Cross-review Codex: sem isto, as atividades da conta A seguem visíveis pelos
  // getters enquanto a conta B carrega as suas — dado de um tenant na tela de
  // outro, ainda que por instantes.
  RESET($state) {
    $state.dataVersion += 1;
    $state.epoch += 1;
    $state.pending = {};
    $state.activities = [];
    $state.requestEpoch += 1;
    // Os contadores NÃO são zerados aqui: as operações em voo ainda vão
    // executar seus `finally`, e zerar agora faria o decremento delas levar o
    // contador da conta nova para baixo de zero.
    $state.loadedAccountId = null;
    $state.requestedAccountId = null;
    $state.isStale = false;
    // Cross-review Codex: sem zerar as flags, uma escrita invalidada pela troca
    // de conta deixa `isCreating`/`isUpdating` presos em true — a tela da conta
    // nova nasce em estado de carregamento que nunca termina.
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
    };
  },
  REMOVE_ACTIVITY($state, id) {
    $state.activities = $state.activities.filter(item => item.id !== id);
  },
};

const actions = {
  get: async ({ commit, rootGetters, state: $state }, payload = {}) => {
    const accountId = requireAccountId(rootGetters, payload.accountId);
    // A leitura também recusa conta inativa: um dispatch atrasado com a conta
    // antiga chegaria a mexer no estado antes mesmo de disparar o request.
    assertAccountIsActive(rootGetters, accountId);
    // Conta diferente da última carga: limpa antes de buscar.
    // Cross-review Codex: condicionar o RESET a `loadedAccountId` já preenchido
    // deixava um buraco — se a PRIMEIRA carga da conta A ainda está em voo,
    // `loadedAccountId` é null, e trocar para B não trocava a época. A resposta
    // de A então chegava válida e populava a tela de B. O que decide é a conta
    // da última leitura pedida, preenchida ou não.
    if ($state.requestedAccountId && $state.requestedAccountId !== accountId) {
      commit('RESET');
    }
    commit('SET_REQUESTED_ACCOUNT', accountId);
    const request = fetchRequestGuard.begin($state, { accountId });
    const requestEpoch = $state.requestEpoch;
    const isCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      fetchRequestGuard.isCurrent($state, request, rootGetters);

    // Cross-review Codex: com um booleano, a listagem invalidada por uma escrita
    // nunca limpava `isFetching` e a tela ficava carregando para sempre. Contar
    // as leituras em voo faz TODA saída — inclusive a invalidada — liberar.
    const version = $state.dataVersion;
    const epoch = $state.epoch;
    commit('BEGIN_FETCH', epoch);
    try {
      const { data } = await pipelineActivitiesAPI.list(payload);
      if (!isCurrent()) return [];
      const activities = data?.payload || data?.activities || data || [];
      // Cross-review Codex: a posse só é registrada QUANDO a leitura deu certo.
      // Marcá-la antes fazia uma falha deixar a lista vazia parecendo carregada.
      // Uma escrita que terminou no meio desta leitura invalida o retrato que
      // acabou de chegar: aplicá-lo desfaria a mudança na tela.
      if (version !== $state.dataVersion) return activities;

      commit('SET_LOADED_ACCOUNT', accountId);
      commit('SET_ACTIVITIES', activities);
      commit('CLEAR_STALE');
      return activities;
    } catch (error) {
      if (isCurrent()) throw error;
      return [];
    } finally {
      commit('END_FETCH', epoch);
    }
  },

  create: async ({ commit, rootGetters, state: $state }, payload = {}) => {
    const accountId = requireExplicitAccountId(rootGetters, payload.accountId);
    // Cross-review Codex: com "só a última vale", duas criações simultâneas
    // faziam a primeira sumir sem erro. Cada escrita é independente — o que
    // decide se o resultado entra é a CONTA, não a ordem de chegada.
    const requestEpoch = $state.requestEpoch;
    const isCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);

    const body = { ...payload };
    delete body.accountId;
    const version = $state.dataVersion;
    const epoch = $state.epoch;
    commit('BEGIN_WRITE', { flag: 'isCreating', epoch });
    try {
      const { data } = await pipelineActivitiesAPI.create(body);
      if (!isCurrent()) return undefined;

      // Cross-review Codex: durante a carga inicial `loadedAccountId` ainda é
      // null. Condicionar o avanço da versão à posse deixava a leitura em voo
      // aplicar um retrato ANTERIOR a esta criação — a atividade recém-criada
      // sumia da tela. A versão avança sempre que a escrita deu certo; só a
      // inserção na coleção depende de a lista já ser desta conta.
      commit('BUMP_VERSION');
      commit('MARK_STALE');
      if (writeBelongsToLoadedList($state, accountId)) {
        commit('ADD_ACTIVITY', data);
      }
      return data;
    } catch (error) {
      // Erro de uma conta que já não é a ativa não sobe para a tela da nova.
      if (isCurrent()) throw error;
      return undefined;
    } finally {
      // Toda saída libera — inclusive a de uma conta que já não é a ativa.
      commit('END_WRITE', { flag: 'isCreating', epoch });
    }
  },

  // As transições compartilham a mesma forma: chamam o endpoint próprio e
  // substituem a atividade pela versão que o servidor devolveu — nunca por uma
  // montada aqui, senão o estado local diverge das regras do backend.
  transition: async (
    { commit, rootGetters, state: $state },
    { id, action, accountId: rawAccountId, ...args } = {}
  ) => {
    if (!TRANSITIONS.includes(action)) {
      throw new TypeError(`unknown activity transition: ${action}`);
    }
    const accountId = requireExplicitAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const isCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);

    const version = $state.dataVersion;
    const epoch = $state.epoch;
    commit('BEGIN_WRITE', { flag: 'isUpdating', epoch });
    try {
      const { data } = await pipelineActivitiesAPI[action](id, args);
      if (!isCurrent()) return undefined;
      commit('BUMP_VERSION');
      commit('MARK_STALE');
      if (writeBelongsToLoadedList($state, accountId)) {
        commit('REPLACE_ACTIVITY', data);
      }
      return data;
    } catch (error) {
      if (isCurrent()) throw error;
      return undefined;
    } finally {
      commit('END_WRITE', { flag: 'isUpdating', epoch });
    }
  },

  delete: async ({ commit, rootGetters, state: $state }, payload = {}) => {
    const { id } = payload;
    const accountId = requireExplicitAccountId(rootGetters, payload.accountId);
    const requestEpoch = $state.requestEpoch;
    const isCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);

    const version = $state.dataVersion;
    const epoch = $state.epoch;
    commit('BEGIN_WRITE', { flag: 'isUpdating', epoch });
    try {
      await pipelineActivitiesAPI.delete(id);
      if (isCurrent()) {
        commit('BUMP_VERSION');
        commit('MARK_STALE');
        if (writeBelongsToLoadedList($state, accountId)) {
          commit('REMOVE_ACTIVITY', id);
        }
      }
    } catch (error) {
      // Erro de uma conta que já não é a ativa não sobe para a tela da nova.
      if (isCurrent()) throw error;
    } finally {
      commit('END_WRITE', { flag: 'isUpdating', epoch });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
